import React, { useEffect, useMemo, useState } from "react";

const SUBCATEGORIES = [
  "how_things_work",
  "how_to_tips",
  "problems_solutions",
  "advice_insights",
  "specials_discounts",
  "stories_experiences",
  "community_relations",
  "humor_personality",
];

function buildQuery(params) {
  const sp = new URLSearchParams();
  Object.entries(params).forEach(([k, v]) => {
    if (v === undefined || v === null || v === "") return;
    sp.set(k, String(v));
  });
  return sp.toString();
}

export default function AdminDesignCenterReview() {
  const [professionId, setProfessionId] = useState(""); // string on purpose (input)
  const [subcategory, setSubcategory] = useState("");
  const [status, setStatus] = useState("");
  const [limit, setLimit] = useState(300);
  const [offset, setOffset] = useState(0);

  const [q, setQ] = useState(""); // keyword search (title/desc/author/tags)
  const [createdAfter, setCreatedAfter] = useState(""); // timestamp filter

  const [loading, setLoading] = useState(false);

  const [total, setTotal] = useState(0);
  const [rows, setRows] = useState([]);
  const [err, setErr] = useState("");

  // Publish state
  const [publishing, setPublishing] = useState(false);
  const [publishResult, setPublishResult] = useState(null); // { imported, failed, results }
  const [approvedQueue, setApprovedQueue] = useState([]); // candidate_ids approved this session
  const [lightboxUrl, setLightboxUrl] = useState(null); // preview_url for enlarged view

  const queryString = useMemo(() => {
    return buildQuery({
      profession_canonical_id: professionId,
      subcategory_key: subcategory,
      status,
      q,
      created_after: createdAfter,
      limit,
      offset,
    });
  }, [professionId, subcategory, status, q, createdAfter, limit, offset]);



  async function fetchCandidates() {
    setLoading(true);
    setErr("");
    try {
      const res = await fetch(`/api/admin/designcenter/candidates?${queryString}`, {
        method: "GET",
        credentials: "include",
        headers: { "Accept": "application/json" },
      });

      if (res.status === 401) {
        setErr("Not authenticated (401). Log in again and retry.");
        setRows([]);
        setTotal(0);
        return;
      }

      if (res.status === 403) {
        setErr("Admin only (403). Your user is not flagged as admin.");
        setRows([]);
        setTotal(0);
        return;
      }

      const data = await res.json();
      if (!res.ok || !data?.ok) {
        setErr(data?.error || `Request failed (${res.status})`);
        setRows([]);
        setTotal(0);
        return;
      }

      setRows(data.rows || []);
      setTotal(Number(data.total || 0));
    } catch (e) {
      setErr(e?.message || "Network error");
      setRows([]);
      setTotal(0);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    fetchCandidates();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [queryString]);

  async function updateStatus(candidateId, action /* 'approve' | 'reject' */) {
    setErr("");
    try {
      const res = await fetch(`/api/admin/designcenter/candidates/${candidateId}/${action}`, {
        method: "POST",
        credentials: "include",
        headers: { "Accept": "application/json" },
      });

      const data = await res.json().catch(() => ({}));

      if (!res.ok || !data?.ok) {
        setErr(data?.error || `Action failed (${res.status})`);
        return;
      }

      // Track approved candidates so the publish button knows about them
      if (action === "approve") {
        setApprovedQueue(prev => [...prev, Number(candidateId)]);
      }

      // Remove from current list immediately (fast review flow)
      setRows(prev => prev.filter(r => String(r.candidate_id) !== String(candidateId)));
      setTotal(prev => Math.max(0, prev - 1));
    } catch (e) {
      setErr(e?.message || "Network error");
    }
  }

  const [approvingAll, setApprovingAll] = useState(false);

  async function approveAll() {
    const ids = rows.filter(r => r.status === "new" || r.status === "approved").map(r => r.candidate_id);
    if (ids.length === 0) return;

    setApprovingAll(true);
    setErr("");
    let approved = 0;

    for (const cid of ids) {
      try {
        const res = await fetch(`/api/admin/designcenter/candidates/${cid}/approve`, {
          method: "POST",
          credentials: "include",
          headers: { "Accept": "application/json" },
        });
        const data = await res.json().catch(() => ({}));
        if (res.ok && data?.ok) approved++;
      } catch (_) {}
    }

    setApprovedQueue(prev => [...prev, ...ids.map(Number)]);
    setRows([]);
    setTotal(0);
    setApprovingAll(false);
    fetchCandidates();
  }

  // Combine: IDs approved this session (queue) + any already-approved rows visible from server
  const publishableIds = useMemo(() => {
    const fromRows = rows.filter(r => r.status === "approved").map(r => Number(r.candidate_id));
    const merged = [...new Set([...approvedQueue, ...fromRows])];
    return merged;
  }, [rows, approvedQueue]);

  async function handlePublish() {
    if (publishableIds.length === 0) return;

    setPublishing(true);
    setPublishResult(null);
    setErr("");

    try {
      const res = await fetch("/api/admin/designcenter/import/run", {
        method: "POST",
        credentials: "include",
        headers: {
          "Accept": "application/json",
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ candidate_ids: publishableIds }),
      });

      const data = await res.json().catch(() => ({}));

      if (!res.ok || !data?.ok) {
        setErr(data?.error || `Publish failed (${res.status})`);
        return;
      }

      setPublishResult(data);
      // Clear the queue — these have been processed
      setApprovedQueue([]);
      // Refresh the list
      fetchCandidates();
    } catch (e) {
      setErr(e?.message || "Network error during publish");
    } finally {
      setPublishing(false);
    }
  }

  const pageStart = total === 0 ? 0 : offset + 1;
  const pageEnd = Math.min(offset + limit, total);

  return (
    <div className="h-full p-4 mx-auto" style={{ width: "80%" }}>
      <div className="flex items-center justify-between mb-3">
        <div>
          <div className="text-lg font-semibold">Design Center — Candidate Review (Admin)</div>
          <div className="text-xs text-gray-500">
            Showing {pageStart}-{pageEnd} of {total}
          </div>
        </div>

        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={approveAll}
            className="px-4 py-1.5 text-sm rounded border border-green-500 text-white bg-green-600 hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed"
            disabled={approvingAll || publishing || loading || rows.length === 0}
            title={`Approve all ${rows.length} candidates in current view`}
          >
            {approvingAll
              ? "Approving All..."
              : `Approve All (${rows.length})`}
          </button>

          <button
            type="button"
            onClick={handlePublish}
            className="px-4 py-1.5 text-sm rounded border border-blue-500 text-white bg-blue-600 hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
            disabled={publishing || loading || publishableIds.length === 0}
            title={publishableIds.length === 0 ? "No approved candidates in current view" : `Download & publish ${publishableIds.length} approved candidates`}
          >
            {publishing
              ? `Publishing ${publishableIds.length}... (do not close)`
              : `Download & Publish (${publishableIds.length})`}
          </button>

          <button
            type="button"
            onClick={() => fetchCandidates()}
            className="px-3 py-1.5 text-sm rounded border border-gray-300 hover:bg-gray-50"
            disabled={loading}
          >
            {loading ? "Loading..." : "Refresh"}
          </button>
        </div>
      </div>

      {/* Publish result banner */}
      {publishResult ? (
        <div className={`mb-3 p-3 rounded border text-sm ${
          publishResult.failed > 0
            ? "border-yellow-300 bg-yellow-50 text-yellow-800"
            : "border-green-300 bg-green-50 text-green-800"
        }`}>
          <div className="flex items-start justify-between">
            <div>
              <div className="font-semibold">
                Published {publishResult.imported} asset{publishResult.imported !== 1 ? "s" : ""} successfully.
                {publishResult.failed > 0 ? ` ${publishResult.failed} failed.` : ""}
              </div>
              {publishResult.failed > 0 && publishResult.results ? (
                <ul className="mt-1 list-disc list-inside text-xs">
                  {publishResult.results
                    .filter(r => r.status === "error")
                    .map(r => (
                      <li key={r.candidate_id}>
                        Candidate #{r.candidate_id}: {r.error}
                      </li>
                    ))}
                </ul>
              ) : null}
            </div>
            <button
              type="button"
              onClick={() => setPublishResult(null)}
              className="text-xs text-gray-500 hover:text-gray-700 ml-3"
            >
              dismiss
            </button>
          </div>
        </div>
      ) : null}

      {/* Filters */}
      <div className="grid grid-cols-1 md:grid-cols-7 gap-2 mb-4">
        <div className="flex flex-col">
          <label className="text-xs text-gray-600 mb-1">Profession ID</label>
          <input
            value={professionId}
            onChange={(e) => { setOffset(0); setProfessionId(e.target.value.replace(/[^\d]/g, "")); }}
            placeholder="e.g. 113"
            className="px-3 py-2 text-sm rounded border border-gray-300"
          />
        </div>

        <div className="flex flex-col">
          <label className="text-xs text-gray-600 mb-1">Search</label>
          <input
            value={q}
            onChange={(e) => { setOffset(0); setQ(e.target.value); }}
            placeholder="e.g. wrench, sink, pipe, drain"
            className="px-3 py-2 text-sm rounded border border-gray-300"
          />
        </div>

        <div className="flex flex-col">
          <label className="text-xs text-gray-600 mb-1">Created After</label>
          <input
            value={createdAfter}
            onChange={(e) => { setOffset(0); setCreatedAfter(e.target.value); }}
            placeholder="YYYY-MM-DD HH:MM:SS.ffffff"
            className="px-3 py-2 text-sm rounded border border-gray-300"
          />
        </div>


        <div className="flex flex-col">
          <label className="text-xs text-gray-600 mb-1">Subcategory</label>
          <select
            value={subcategory}
            onChange={(e) => { setOffset(0); setSubcategory(e.target.value); }}
            className="px-3 py-2 text-sm rounded border border-gray-300 bg-white"
          >
            <option value="">All</option>
            {SUBCATEGORIES.map(k => (
              <option key={k} value={k}>{k}</option>
            ))}
          </select>
        </div>

        <div className="flex flex-col">
          <label className="text-xs text-gray-600 mb-1">Status</label>
          <select
            value={status}
            onChange={(e) => { setOffset(0); setStatus(e.target.value); }}
            className="px-3 py-2 text-sm rounded border border-gray-300 bg-white"
          >
            <option value="">All</option>
            <option value="new">new</option>
            <option value="approved">approved</option>
            <option value="rejected">rejected</option>
            <option value="imported">imported</option>
          </select>
        </div>

        <div className="flex flex-col">
          <label className="text-xs text-gray-600 mb-1">Limit</label>
          <select
            value={limit}
            onChange={(e) => { setOffset(0); setLimit(Number(e.target.value)); }}
            className="px-3 py-2 text-sm rounded border border-gray-300 bg-white"
          >
            {[10, 25, 50, 100, 200, 300].map(n => (
              <option key={n} value={n}>{n}</option>
            ))}
          </select>
        </div>

        <div className="flex flex-col">
          <label className="text-xs text-gray-600 mb-1">Page</label>
          <div className="flex gap-2">
            <button
              type="button"
              onClick={() => setOffset(prev => Math.max(0, prev - limit))}
              className="flex-1 px-3 py-2 text-sm rounded border border-gray-300 hover:bg-gray-50 disabled:opacity-50"
              disabled={loading || offset === 0}
            >
              Prev
            </button>
            <button
              type="button"
              onClick={() => setOffset(prev => (prev + limit < total ? prev + limit : prev))}
              className="flex-1 px-3 py-2 text-sm rounded border border-gray-300 hover:bg-gray-50 disabled:opacity-50"
              disabled={loading || offset + limit >= total}
            >
              Next
            </button>
          </div>
        </div>
      </div>

      {err ? (
        <div className="mb-3 p-3 rounded border border-red-200 bg-red-50 text-sm text-red-700">
          {err}
        </div>
      ) : null}

      {/* Results */}
      <div className="space-y-3">
        {rows.map((r) => {
          const candidateId = r.candidate_id;
          return (
            <div
              key={String(candidateId)}
              className="w-full rounded border border-gray-200 bg-white p-3"
            >
              <div className="flex gap-3">
                {/* Preview — click to enlarge */}
                <div
                  className="w-28 h-20 rounded overflow-hidden border border-gray-200 bg-gray-50 flex-shrink-0 cursor-pointer hover:ring-2 hover:ring-blue-400 transition-shadow"
                  onClick={() => setLightboxUrl(r.preview_url || r.thumb_url)}
                  title="Click to enlarge"
                >
                  {r.thumb_url ? (
                    <img
                      src={r.thumb_url}
                      alt=""
                      className="w-full h-full object-cover"
                      loading="lazy"
                      referrerPolicy="no-referrer"
                    />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center text-xs text-gray-400">
                      no thumb
                    </div>
                  )}
                </div>

                {/* Meta */}
                <div className="flex-1 min-w-0">
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0">
                      <div className="text-sm font-semibold truncate">
                        {r.title || "(no title)"}
                      </div>
                        <div className="text-xs text-gray-600 mt-0.5">
                        #{candidateId} · prof {r.profession_canonical_id}
                        {r.profession_name ? ` (${r.profession_name})` : ""} · {r.subcategory_key} · {r.asset_type} · {r.source}
                        </div>

                      <div className="text-xs text-gray-500 mt-1">
                        Author: {r.author_name || "—"} · Attribution: {r.attribution_required ? "yes" : "no"}
                      </div>
                    </div>

                    {/* Actions */}
                    <div className="flex items-center gap-2 flex-shrink-0">
                      <button
                        type="button"
                        onClick={() => updateStatus(candidateId, "approve")}
                        className="px-3 py-1.5 text-sm rounded border border-green-300 text-green-700 hover:bg-green-50"
                        disabled={loading}
                      >
                        Approve
                      </button>

                      <button
                        type="button"
                        onClick={() => updateStatus(candidateId, "reject")}
                        className="px-3 py-1.5 text-sm rounded border border-red-300 text-red-700 hover:bg-red-50"
                        disabled={loading}
                      >
                        Reject
                      </button>
                    </div>
                  </div>

                  <div className="flex items-center gap-3 mt-2 text-xs">
                    {r.source_page_url ? (
                      <a
                        href={r.source_page_url}
                        target="_blank"
                        rel="noreferrer"
                        className="text-blue-600 hover:underline"
                      >
                        Open source page
                      </a>
                    ) : null}

                    {r.preview_url ? (
                      <a
                        href={r.preview_url}
                        target="_blank"
                        rel="noreferrer"
                        className="text-blue-600 hover:underline"
                      >
                        Open preview
                      </a>
                    ) : null}

                    <span className="text-gray-400">
                      status: {r.status || "—"}
                    </span>
                  </div>

                  {r.description ? (
                    <div className="mt-2 text-xs text-gray-700">
                      {r.description}
                    </div>
                  ) : null}
                </div>
              </div>
            </div>
          );
        })}

        {!loading && rows.length === 0 ? (
          <div className="text-sm text-gray-500">No results for current filters.</div>
        ) : null}
      </div>

      {/* Lightbox overlay */}
      {lightboxUrl ? (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 cursor-pointer"
          onClick={() => setLightboxUrl(null)}
        >
          <div className="relative max-w-4xl max-h-[90vh] p-2">
            <img
              src={lightboxUrl}
              alt=""
              className="max-w-full max-h-[85vh] object-contain rounded shadow-2xl"
              referrerPolicy="no-referrer"
            />
            <div className="absolute top-4 right-4 text-white text-sm bg-black/50 px-3 py-1 rounded">
              click anywhere to close
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
}
